<template>
  <s-modal
    v-model="showModal"
    cancel-confirm-message-code="MC006"
    ok-confirm-message-code="MC048"
    size="md"
    :confirm-type="confirmationTypes.YES_NO"
    :title="$t('engineering.commutator-maintenance.edit-values')"
    :ok-title="$t('general.confirm')"
    :ok-disabled="$v.$error"
    :is-loading="loading"
    @cancel="closeModal"
    @ok="save"
    @show="show"
  >
    <b-row>
      <b-col>
        <b-form-group>
          <label>{{ getFieldLabel('totalServiceTime') }}*</label>
          <s-number-input
            class="text-right col-sm-3"
            v-model="$v.totalServiceTime.$model"
            id="total-service-time-input"
            ref="sMoneyTotalServiceTime"
            :disabled="loading"
            :validation="$v.totalServiceTime"
            :invalid="$v.totalServiceTime.$error"
            :numeric-options="customNumericOptions"
          />
          <b-form-invalid-feedback
            v-if="$v.totalServiceTime.$error"
            id="total-service-time-feedback"
          >
            <span v-html="getValidationFeedbacks('totalServiceTime')"></span>
          </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group>
          <label>{{ getFieldLabel('performedMaintenanceNumber') }}*</label>
          <s-number-input
            class="text-right col-sm-3"
            v-model="$v.performedMaintenanceNumber.$model"
            id="performed-maintenance-number-input"
            ref="sMoneyPerformedMaintenanceNumber"
            :disabled="loading"
            :validation="$v.performedMaintenanceNumber"
            :invalid="$v.performedMaintenanceNumber.$error"
            :numeric-options="customNumericOptions"
          />
          <b-form-invalid-feedback
            v-if="$v.performedMaintenanceNumber.$error"
            id="performed-maintenance-number-feedback"
          >
            <span v-html="getValidationFeedbacks('performedMaintenanceNumber')"></span>
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>
  </s-modal>
</template>

<script>
import {
  minValue,
  maxValue,
  maxLength,
  required,
} from 'vuelidate/lib/validators';
import Service
  from '@/domain/services/installation/engineering/commutatorMaintenance/commutatorMaintenance';
import LoadingMixin from '@/services/loadingMixin';
import fieldsDefinitions
  from '@/domain/data/installation/engineering/commutatorMaintenance/editValuesFieldsDefinitions';
import confirmationType from '@/constants/confirmationTypes';

export default {
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    commutatorId: {
      type: Number,
      required: true,
    },
    totalServiceTimeValue: {
      type: [String, Number],
      default: '0',
    },
    performedMaintenanceNumberValue: {
      type: [String, Number],
      default: '0',
    },
  },
  mixins: [LoadingMixin],
  data: () => ({
    totalServiceTime: 0,
    performedMaintenanceNumber: 0,
    confirmationTypes: confirmationType,
    isEditing: false,
  }),
  validations: {
    totalServiceTime: {
      required,
      maxLength: maxLength(fieldsDefinitions.totalServiceTime.maxLength),
      minValue: minValue(fieldsDefinitions.totalServiceTime.min),
      maxValue: maxValue(fieldsDefinitions.totalServiceTime.max),
    },
    performedMaintenanceNumber: {
      required,
      maxLength: maxLength(fieldsDefinitions.performedMaintenanceNumber.maxLength),
      minValue: minValue(fieldsDefinitions.performedMaintenanceNumber.min),
      maxValue: maxValue(fieldsDefinitions.performedMaintenanceNumber.max),
    },
  },
  computed: {
    showModal: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    customNumericOptions() {
      return {
        decimalPlaces: 0,
      };
    },
  },
  methods: {
    validateFieldName(fieldName) {
      if (!this.$v[fieldName] || !fieldsDefinitions[fieldName]) {
        throw new Error('Invalid field name given!');
      }
    },
    getInputOptions(fieldName) {
      this.validateFieldName(fieldName);

      return {
        state: this.$bsStateValidator(this.$v[fieldName].$error),
        required: fieldsDefinitions[fieldName].required,
        range: {
          min: fieldsDefinitions[fieldName].min,
          max: fieldsDefinitions[fieldName].max,
        },
        maxLength: fieldsDefinitions[fieldName].maxLengthMask,
      };
    },
    getFieldLabel(fieldName) {
      this.validateFieldName(fieldName);

      return this.$t(fieldsDefinitions[fieldName].label);
    },
    getValidationFeedbacks(fieldName) {
      this.validateFieldName(fieldName);

      let validationFeedback = '';

      if (!this.$v[fieldName].required) {
        validationFeedback += this.$t('messages.MA001.message',
          { NomeDoCampo: this.$t(fieldsDefinitions[fieldName].label) });
      }

      if (!this.$v[fieldName].minValue
        || !this.$v[fieldName].maxValue) {
        validationFeedback += validationFeedback !== '' ? '<br>' : '';
        validationFeedback += this.$t('messages.MA086.message',
          {
            minValue: this.$n(fieldsDefinitions[fieldName].min),
            maxValue: this.$n(fieldsDefinitions[fieldName].max),
          });
      }

      if (!this.$v[fieldName].maxLength) {
        validationFeedback += validationFeedback !== '' ? '<br>' : '';
        validationFeedback += this.$t('messages.MA051.message',
          {
            fieldName: this.$t(fieldsDefinitions[fieldName].label),
            maxLength: fieldsDefinitions[fieldName].maxLength,
          });
      }

      return validationFeedback;
    },
    clearFields() {
      this.totalServiceTime = 0;
      this.performedMaintenanceNumber = 0;
    },
    show() {
      if (!this.isEditing) {
        this.totalServiceTime = this.totalServiceTime
          || Number(this.totalServiceTimeValue);
        this.performedMaintenanceNumber = this.performedMaintenanceNumber
          || Number(this.performedMaintenanceNumberValue);
      }

      this.isEditing = true;
    },
    closeModal() {
      this.clearFields();
      this.isEditing = false;
      this.showModal = false;
    },
    save() {
      this.startLoading();

      const { assetModuleId } = this.$route.params;
      const params = {
        totalServiceTime: this.totalServiceTime,
        performedMaintenanceNumber: this.performedMaintenanceNumber,
      };

      Service.setCommutatorValues(assetModuleId, this.commutatorId, params)
        .then(() => {
          this.$emit('commutator-values-edited', params);
          this.$pushAlert({ code: 'MS032' });
          this.closeModal();
        })
        .finally(() => {
          this.endLoading();
        });
    },
  },
};
</script>
